import { useState } from "react";
import { Loader2, Scale, TrendingUp } from "lucide-react";
import { useBalance, usePyg } from "../api";
import { useAccountingStore } from "../store";
import { AccountingHeader } from "../components/AccountingHeader";
import type { SumaSaldo } from "../types";

function eur(n: number): string {
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);
}

function DetalleTabla({ rows }: { rows: SumaSaldo[] }) {
  if (rows.length === 0) {
    return <p className="text-xs text-slate-400 py-2">Sin movimientos.</p>;
  }
  return (
    <table className="w-full text-sm">
      <tbody>
        {rows.map((r) => (
          <tr key={r.codigo} className="border-t border-slate-100">
            <td className="py-1.5 font-mono text-xs text-slate-500 w-24">{r.codigo}</td>
            <td className="py-1.5 text-slate-700 truncate">{r.nombre}</td>
            <td className="py-1.5 text-right font-mono font-bold text-slate-900">
              {eur(Math.abs(r.saldo))}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function InformesPage() {
  const { emisorId, ejercicio } = useAccountingStore();
  const desde = `${ejercicio}-01-01`;
  const hasta = `${ejercicio}-12-31`;
  const [tab, setTab] = useState<"balance" | "pyg">("balance");

  const { data: balance, isLoading: lb } = useBalance(emisorId, desde, hasta);
  const { data: pyg, isLoading: lp } = usePyg(emisorId, desde, hasta);

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0">
      <AccountingHeader title="Contabilidad" subtitle="Informes financieros del ejercicio." />

      {/* Sub-tabs informe */}
      <div className="flex gap-2 shrink-0">
        <button
          onClick={() => setTab("balance")}
          className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition ${
            tab === "balance"
              ? "bg-brand-700 text-white shadow-soft"
              : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
          }`}
        >
          <Scale size={16} /> Balance de situación
        </button>
        <button
          onClick={() => setTab("pyg")}
          className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition ${
            tab === "pyg"
              ? "bg-brand-700 text-white shadow-soft"
              : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
          }`}
        >
          <TrendingUp size={16} /> Pérdidas y ganancias
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        {/* BALANCE */}
        {tab === "balance" &&
          (lb ? (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 className="animate-spin" size={18} /> Cargando balance…
            </div>
          ) : balance ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50 to-white p-5 shadow-soft">
                  <div className="text-xs font-black uppercase tracking-wider text-sky-700">
                    Activo
                  </div>
                  <div className="text-3xl font-black text-slate-900 mt-1">
                    {eur(balance.activo)}
                  </div>
                </div>
                <div className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-white p-5 shadow-soft">
                  <div className="text-xs font-black uppercase tracking-wider text-violet-700">
                    Patrimonio Neto + Pasivo
                  </div>
                  <div className="text-3xl font-black text-slate-900 mt-1">
                    {eur(balance.pn_pasivo_con_resultado)}
                  </div>
                  {Math.abs(balance.resultado_ejercicio) > 0.005 && (
                    <div className="text-xs text-slate-500 mt-1">
                      Incluye resultado del ejercicio {eur(balance.resultado_ejercicio)}
                    </div>
                  )}
                </div>
              </div>

              {/* Indicador de cuadre */}
              <div
                className={`flex items-center justify-between rounded-xl px-4 py-2.5 text-sm font-bold ${
                  balance.cuadra
                    ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
                    : "bg-amber-50 border border-amber-200 text-amber-800"
                }`}
              >
                <span>
                  {balance.cuadra
                    ? "✓ El balance cuadra (Activo = PN + Pasivo + Resultado)"
                    : `⚠ Descuadre de ${eur(balance.descuadre)}`}
                </span>
                <span className="font-mono">
                  {eur(balance.activo)} = {eur(balance.pn_pasivo_con_resultado)}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
                  <h3 className="font-black text-slate-900 mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-sky-500" /> Activo (cuentas deudoras)
                  </h3>
                  <DetalleTabla rows={balance.detalle_activo} />
                </div>
                <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
                  <h3 className="font-black text-slate-900 mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-violet-500" /> PN + Pasivo (cuentas
                    acreedoras)
                  </h3>
                  <DetalleTabla rows={balance.detalle_pasivo} />
                  {Math.abs(balance.resultado_ejercicio) > 0.005 && (
                    <div className="mt-2 pt-2 border-t-2 border-violet-200 flex items-center justify-between text-sm">
                      <span className="font-bold text-violet-800">
                        Resultado del ejercicio
                      </span>
                      <span className="font-mono font-black text-slate-900">
                        {eur(balance.resultado_ejercicio)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null)}

        {/* PYG */}
        {tab === "pyg" &&
          (lp ? (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 className="animate-spin" size={18} /> Cargando PyG…
            </div>
          ) : pyg ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 shadow-soft">
                  <div className="text-xs font-black uppercase tracking-wider text-emerald-700">
                    Ingresos
                  </div>
                  <div className="text-2xl font-black text-slate-900 mt-1">{eur(pyg.ingresos)}</div>
                </div>
                <div className="rounded-2xl border border-rose-200 bg-gradient-to-br from-rose-50 to-white p-5 shadow-soft">
                  <div className="text-xs font-black uppercase tracking-wider text-rose-700">
                    Gastos
                  </div>
                  <div className="text-2xl font-black text-slate-900 mt-1">{eur(pyg.gastos)}</div>
                </div>
                <div
                  className={`rounded-2xl border p-5 shadow-soft ${
                    pyg.resultado >= 0
                      ? "border-brand-700/20 bg-gradient-to-br from-brand-700 to-emerald-600 text-white"
                      : "border-rose-300 bg-gradient-to-br from-rose-600 to-rose-500 text-white"
                  }`}
                >
                  <div className="text-xs font-black uppercase tracking-wider opacity-90">
                    Resultado del ejercicio
                  </div>
                  <div className="text-2xl font-black mt-1">{eur(pyg.resultado)}</div>
                  <div className="text-xs opacity-80 mt-0.5">
                    BAI {eur(pyg.bai)} · Impuestos {eur(pyg.impuestos)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
                  <h3 className="font-black text-slate-900 mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" /> Ingresos (grupo 7)
                  </h3>
                  <DetalleTabla rows={pyg.detalle_ingresos} />
                </div>
                <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
                  <h3 className="font-black text-slate-900 mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-rose-500" /> Gastos (grupo 6)
                  </h3>
                  <DetalleTabla rows={pyg.detalle_gastos} />
                </div>
              </div>
            </div>
          ) : null)}
      </div>
    </div>
  );
}
